// React
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useIsScrollable } from './useIsScrollable';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';

// UI primitives
import { Accordion } from '@/components/ui/accordion';
import { Counter } from '@/components/ui/counter';

// Local components
import { DataTableTabs, type DataTableTab } from '../DataTable/DataTableTabs';
import { PreviewSheet } from '../PreviewSheet';
import { ActivityCentreHeader } from './ActivityCentreHeader';
import { ErrorState } from './ErrorState';
import { InfiniteScrollSentinel } from '../common/InfiniteScrollSentinel';
import { JobCard } from './JobCard';
import { JobCardSkeleton } from './JobCardSkeleton';
import { ScrollbarHairlines } from './ScrollbarHairlines';

// Icons
import { ActivityCentreIcon } from '@components/icons/ActivityCentreIcon';
import { EmptyBoxIcon } from '@components/icons/EmptyBoxIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useAsyncTasks } from '../../hooks/useAsyncTasks';
import { useTranslation } from '../../hooks/useTranslation';
import { useActivityCentrePanel } from './useActivityCentrePanel';

// Types
import { asyncTasksQueryKeys, type AsyncTasksListParams } from '@api/asyncTasks';
import {
  ACTIVITY_CENTRE_SHEET_WIDTH,
  ACTIVITY_CENTRE_TABS,
  CUSTOM_SCROLLBAR_CLASSES,
  type ActivityCentreTab,
} from './constants';

const LOADING_SKELETON_COUNT = 3;

const TAB_TO_STATUS: Record<ActivityCentreTab, AsyncTasksListParams['status']> = {
  processing: 'active',
  completed: 'completed',
};

interface ActivityCentreProps {
  // When rendered in the collapsed desktop sidebar rail, hide the label and
  // count so only the icon shows — matching every sibling nav item. Defaults
  // to false so the mobile drawer and standalone Rails mounts are unaffected.
  collapsed?: boolean;
  // Host-supplied row class for the trigger button. Defaults to the desktop
  // nav-link treatment so the desktop sidebar and the standalone registry
  // mount are unaffected by hosts that override it (e.g. the mobile drawer).
  triggerClassName?: string;
  // Host-supplied icon class. Defaults to the desktop 15px icon.
  iconClassName?: string;
  // Host-supplied count renderer, so a host can swap in its own badge
  // component (e.g. MobileCountBadge) instead of the desktop Counter.
  renderCount?: (count: number) => ReactNode;
}

export function ActivityCentre({
  collapsed = false,
  triggerClassName = 'nav-link w-full',
  iconClassName = 'size-5 shrink-0',
  renderCount,
}: ActivityCentreProps = {}) {
  const { t } = useTranslation();
  const { open, setOpen, tab, setTab } = useActivityCentrePanel();
  const queryClient = useQueryClient();

  // Always-enabled count query drives the sidebar count so it reflects live
  // activity before the user ever opens the sheet. Shares the query key with
  // the Processing tab, so no duplicate fetch when that tab is active.
  const { data: activeData } = useAsyncTasks({ status: 'active' });
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAsyncTasks(
    { status: TAB_TO_STATUS[tab] },
    { enabled: open },
  );

  const tabpanelRef = useRef<HTMLDivElement>(null);
  const [setTabpanelRef, isScrollable] = useIsScrollable<HTMLDivElement>(tabpanelRef);

  const tasks = data?.pages.flatMap((p) => p.async_tasks) ?? [];
  const count = activeData?.pages[0]?.pagination.total ?? 0;
  const countNode = renderCount ? (
    renderCount(count)
  ) : (
    <Counter
      value={count}
      className={cn('ml-auto', collapsed && 'hidden')}
      data-sidebar-target="label"
      data-count-empty={count === 0 ? 'true' : undefined}
    />
  );

  const isRefreshing =
    useIsFetching({ queryKey: asyncTasksQueryKeys.all }) > 0;
  const handleRefresh = useCallback(() => {
    // resetQueries (vs. invalidateQueries) clears cached data so the tabpanel
    // falls back to isLoading → skeleton during the refetch, not a silent
    // background swap with placeholderData.
    queryClient.resetQueries({ queryKey: asyncTasksQueryKeys.all });
  }, [queryClient]);

  const tabs = useMemo<readonly DataTableTab<ActivityCentreTab>[]>(
    () => ACTIVITY_CENTRE_TABS.map((key) => ({ key, label: t(`tabs.${key}`) })),
    [t],
  );

  return (
    <>
      <button
        type="button"
        className={cn(triggerClassName, open && 'bg-dinnerplate-white-light')}
        aria-label={t('trigger_aria_label')}
        aria-expanded={open}
        data-sidebar-navigation-target="navItem"
        onClick={() => setOpen(true)}
      >
        <ActivityCentreIcon className={iconClassName} aria-hidden="true" />
        <span
          className={cn('nav-text flex-1 text-left', collapsed && 'hidden')}
          data-sidebar-target="label"
        >
          {t('trigger_label')}
        </span>
        {countNode}
      </button>

      <PreviewSheet
        open={open}
        onOpenChange={setOpen}
        width={ACTIVITY_CENTRE_SHEET_WIDTH}
        header={
          <ActivityCentreHeader
            count={count}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        }
        contentClassName="relative flex flex-col p-0 md:p-0 overflow-hidden"
      >
        {isScrollable && <ScrollbarHairlines />}
        <div
          ref={setTabpanelRef}
          role="tabpanel"
          id={`activity-centre-tabpanel-${tab}`}
          aria-labelledby={`activity-centre-tab-${tab}`}
          className={cn(
            'flex-1 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain [scrollbar-gutter:stable] px-8 pb-8',
            CUSTOM_SCROLLBAR_CLASSES,
          )}
        >
          <DataTableTabs
            tabs={tabs}
            value={tab}
            onValueChange={setTab}
            className="sticky top-0 z-20 -mx-8 bg-off-white px-8 pt-8 pb-8"
          />
          {isLoading ? (
            <div className="flex flex-col gap-8">
              {Array.from({ length: LOADING_SKELETON_COUNT }, (_, i) => (
                <JobCardSkeleton key={i} />
              ))}
            </div>
          ) : isError ? (
            <ErrorState />
          ) : tasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <EmptyBoxIcon aria-hidden="true" />
              <p className="font-inter text-xs font-normal leading-tight text-grey antialiased">
                {t('empty_state')}
              </p>
            </div>
          ) : (
            <>
              <Accordion type="multiple" className="flex flex-col gap-8 w-full">
                {tasks.map((task) => (
                  <JobCard key={task.id} task={task} />
                ))}
                {isFetchingNextPage && <JobCardSkeleton />}
              </Accordion>
              <InfiniteScrollSentinel
                enabled={hasNextPage && !isFetchingNextPage}
                onIntersect={fetchNextPage}
                rootRef={tabpanelRef}
              />
            </>
          )}
        </div>
      </PreviewSheet>
    </>
  );
}

export default ActivityCentre;
